import { Info, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SettingRow } from '@/components/ui/setting-row';
import { Switch } from '@/components/ui/switch';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

const SESSION_BUDGET_URL = '/mgmt/api/v1/Organizations/session-budget';

const centsToDollarsText = (cents: number | null | undefined) =>
    cents ? String(cents / 100) : '';

const dollarsTextToCents = (text: string) => {
    const dollars = Number(text);
    return text.trim() !== '' && dollars > 0 ? Math.round(dollars * 100) : null;
};

export const SessionBudgetTab: React.FC = () => {
    const [enabled, setEnabled] = useState(false);
    const [dollarsText, setDollarsText] = useState('');
    const [raiseDollarsText, setRaiseDollarsText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetch(SESSION_BUDGET_URL, { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (cancelled) return;
                setEnabled(!!data?.budgetCents);
                setDollarsText(centsToDollarsText(data?.budgetCents));
                setRaiseDollarsText(centsToDollarsText(data?.raiseCents));
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const budgetCents = dollarsTextToCents(dollarsText);
    const raiseCents = dollarsTextToCents(raiseDollarsText);
    const canSave = !enabled || (budgetCents !== null && raiseCents !== null);

    const budgetFields = [
        {
            id: 'session-budget-dollars',
            label: 'Session budget (USD)',
            description:
                'New chats suggest starting a new chat once they have used this amount.',
            placeholder: '1.00',
            value: dollarsText,
            onChange: setDollarsText
        },
        {
            id: 'session-budget-raise-dollars',
            label: 'Raise when continuing (USD)',
            description:
                'How much the limit grows when a user keeps writing in a long chat.',
            placeholder: '10.00',
            value: raiseDollarsText,
            onChange: setRaiseDollarsText
        }
    ];

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch(SESSION_BUDGET_URL, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    budgetCents: enabled ? budgetCents : null,
                    raiseCents: enabled ? raiseCents : null
                })
            });
            if (!res.ok) {
                const detail = await res.json().catch(() => null);
                throw new Error(
                    detail?.detail || 'Failed to save session budget'
                );
            }
            toast.success('Session budget saved');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to save session budget');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <SettingRow
                label="Enable session budget"
                description="When off, chats run without a cost limit. Scheduled jobs are never limited."
                htmlFor="session-budget-enabled"
                wholeRowClickable
                className="border-b-0"
            >
                <Switch
                    id="session-budget-enabled"
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    disabled={loading || saving}
                />
            </SettingRow>
            {enabled && (
                <TooltipProvider>
                    <div className="space-y-6 max-w-xl">
                        {budgetFields.map(
                            ({
                                id,
                                label,
                                description,
                                placeholder,
                                value,
                                onChange
                            }) => (
                                <div key={id} className="space-y-2">
                                    <div className="flex items-center gap-1.5">
                                        <Label
                                            htmlFor={id}
                                            className="text-(length:--fs-subtitle) leading-(--lh-subtitle) text-foreground"
                                        >
                                            {label}
                                            <span className="ml-1 text-destructive">
                                                *
                                            </span>
                                        </Label>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon-compact"
                                                    aria-label={`About ${label}`}
                                                    className="rounded-sm w-5 h-5"
                                                >
                                                    <Info />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-xs">
                                                {description}
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                    <Input
                                        id={id}
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={value}
                                        onChange={(e) =>
                                            onChange(e.target.value)
                                        }
                                        placeholder={placeholder}
                                        required
                                        disabled={loading || saving}
                                    />
                                </div>
                            )
                        )}
                    </div>
                </TooltipProvider>
            )}
            <Button
                onClick={handleSave}
                disabled={loading || saving || !canSave}
            >
                {saving ? (
                    <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Saving...
                    </>
                ) : (
                    'Save'
                )}
            </Button>
        </div>
    );
};
